import * as React from 'react';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import { minutesToTimeString, timeStringToMinutes } from 'utils/time';
import { isEqual } from 'lodash';
import { logDifference } from 'utils/helper';
import { BetreuerWorkDay, updateBetreuerWorkDay, useBetreuerWorkDayOne } from 'api/betreuerWorkDay';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { ContextualMenu, Dialog, DialogFooter, DialogType, PrimaryButton, TextField } from '@fluentui/react';
import { formStyles } from 'styles/formStyles';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import moment from 'moment-timezone';
import { BetreuerWorkDayAbsent } from 'formComponents/lookups/BetreuerWorkDayAbsent';
import { useBetreuerOne } from 'api/betreuer';

const styles = mergeStyleSets({
    worktimes: {
        marginTop: 15,
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr) 2fr',
        gridColumnGap: 10,
        gridRowGap: 5,
        minHeight: 200,
    },
});

export interface BetreuerWorkDayEditData {
    betrNr: number;
}

interface BetreuerWorkDayEditProps {
    betrNr: number;
    workYear: number;
    workMonth: number;
    workDay: number;
    onClose: (refetch: boolean) => void;
}

export const BetreuerWorkDayEdit: React.FC<BetreuerWorkDayEditProps> = ({
    betrNr,
    workYear,
    workMonth,
    workDay,
    onClose,
}) => {
    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const isMounted = useIsMounted();

    const betreuerOne = useBetreuerOne({
        nr: betrNr,
        withBetreuerWorkTimeList: true,
    });

    const betreuerWorkDayOne = useBetreuerWorkDayOne(
        {
            betrNr,
            workYear,
            workMonth,
            workDay,
        },
        {},
    );

    const [betreuerWorkDay, setBetreuerWorkDay] = React.useState<BetreuerWorkDay | null>(null);

    React.useEffect(() => {
        if (
            !betreuerOne.isLoading &&
            !betreuerOne.isFetching &&
            !betreuerWorkDayOne.isLoading &&
            !betreuerWorkDayOne.isFetching &&
            betreuerWorkDay === null
        ) {
            if (betreuerWorkDayOne.data) {
                let { startTime1, stopTime1, startTime2, stopTime2 } = betreuerWorkDayOne.data;

                if (!betreuerWorkDayOne.data.isEdited) {
                    const weekday = moment(`${workYear}-${workMonth}-${workDay}`).isoWeekday() - 1;

                    const workTime1 = betreuerOne.data?.betreuerWorkTimeList.find(
                        (wt) => wt.weekday === weekday && wt.block === 0,
                    );
                    const workTime2 = betreuerOne.data?.betreuerWorkTimeList.find(
                        (wt) => wt.weekday === weekday && wt.block === 1,
                    );
                    if (workTime1) {
                        startTime1 = workTime1.startTime;
                        stopTime1 = workTime1.stopTime;
                    }
                    if (workTime2) {
                        startTime2 = workTime2.startTime;
                        stopTime2 = workTime2.stopTime;
                    }
                }

                setBetreuerWorkDay({
                    ...betreuerWorkDayOne.data,
                    startTime1,
                    startTime1S: minutesToTimeString(startTime1) || '',
                    stopTime1,
                    stopTime1S: minutesToTimeString(stopTime1) || '',
                    break1S: minutesToTimeString(betreuerWorkDayOne.data.break1) || '',
                    startTime2,
                    startTime2S: minutesToTimeString(startTime2) || '',
                    stopTime2,
                    stopTime2S: minutesToTimeString(stopTime2) || '',
                    break2S: minutesToTimeString(betreuerWorkDayOne.data.break2) || '',
                });

                setSavingState(SavingState.notChanged);
            }
        }
    }, [workYear, workMonth, workDay, betreuerWorkDay, betreuerOne, betreuerWorkDayOne]);

    React.useEffect(() => {
        if (
            savingState === SavingState.notChanged &&
            !betreuerOne.isLoading &&
            !betreuerOne.isFetching &&
            !betreuerWorkDayOne.isLoading &&
            !betreuerWorkDayOne.isFetching &&
            betreuerWorkDayOne.data &&
            betreuerWorkDay !== null
        ) {
            const cleanedBetreuerWorkDay = {
                betrNr: betreuerWorkDay.betrNr,
                workYear: betreuerWorkDay.workYear,
                workMonth: betreuerWorkDay.workMonth,
                workDay: betreuerWorkDay.workDay,
                startTime1: betreuerWorkDay.startTime1,
                stopTime1: betreuerWorkDay.stopTime1,
                break1: betreuerWorkDay.break1,
                startTime2: betreuerWorkDay.startTime2,
                stopTime2: betreuerWorkDay.stopTime2,
                break2: betreuerWorkDay.break2,
                note: betreuerWorkDay.note,
                absent: betreuerWorkDay.absent,
                isEdited: betreuerWorkDay.isEdited,
            };

            if (!isEqual(betreuerWorkDayOne.data, cleanedBetreuerWorkDay)) {
                setSavingState(SavingState.notSaved);
                logDifference('betreuerWorkDay', cleanedBetreuerWorkDay, betreuerWorkDayOne.data);
            }
        }
    }, [savingState, betreuerOne, betreuerWorkDayOne, betreuerWorkDay]);

    const validate = React.useCallback((): boolean => {
        let error = '';
        if (betreuerWorkDay) {
            const startTime1 = betreuerWorkDay.startTime1 === undefined ? null : betreuerWorkDay.startTime1;
            const stopTime1 = betreuerWorkDay.stopTime1 === undefined ? null : betreuerWorkDay.stopTime1;
            const startTime2 = betreuerWorkDay.startTime2 === undefined ? null : betreuerWorkDay.startTime2;
            const stopTime2 = betreuerWorkDay.stopTime2 === undefined ? null : betreuerWorkDay.stopTime2;

            if (startTime1 === null && stopTime1 !== null) {
                error = 'Start-Zeit nicht gesetzt (Vormittag).';
            } else if (startTime1 !== null && stopTime1 === null) {
                error = 'Ende-Zeit nicht gesetzt (Vormittag).';
            } else if (startTime1 !== null && stopTime1 !== null) {
                if (stopTime1 < startTime1) {
                    error = 'Ende-Zeit vor Start-Zeit (Vormittag).';
                } else if (stopTime1 === startTime1) {
                    error = 'Ende-Zeit gleich Start-Zeit (Vormittag).';
                }
            }

            if (startTime2 === null && stopTime2 !== null) {
                error = 'Start-Zeit nicht gesetzt (Nachmittag).';
            } else if (startTime2 !== null && stopTime2 === null) {
                error = 'Ende-Zeit nicht gesetzt (Nachmittag).';
            } else if (startTime2 !== null && stopTime2 !== null) {
                if (stopTime2 < startTime2) {
                    error = 'Ende-Zeit vor Start-Zeit (Nachmittag).';
                } else if (stopTime2 === startTime2) {
                    error = 'Ende-Zeit gleich Start-Zeit (Nachmittag).';
                }
            }

            if (stopTime1 !== null && startTime2 !== null && startTime2 < stopTime1) {
                error = 'Start-Zeit (Nachmittag) vor Ende-Zeit (Vormittag)';
            }
        }

        if (error) {
            setValidationError(error);
            return false;
        }

        if (validationError !== null) {
            setValidationError(null);
        }

        return true;
    }, [betreuerWorkDay, validationError]);

    const save = React.useCallback(() => {
        if (!betreuerWorkDay) {
            return;
        }

        if (savingState !== SavingState.saving) {
            if (validate()) {
                setSavingState(SavingState.saving);
                updateBetreuerWorkDay({ ...betreuerWorkDay, isEdited: true })
                    .then(() => {
                        setSavingState(SavingState.saved);
                        if (isMounted) {
                            setSavingState(SavingState.notChanged);
                        }

                        onClose(true);
                    })
                    .catch((error) => {
                        console.error(error);
                        setSavingState(SavingState.error);
                    });
            }
        }
    }, [betreuerWorkDay, isMounted, onClose, savingState, validate]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    const renderTextField = (column: keyof BetreuerWorkDay, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(betreuerWorkDay?.[column] || '') as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled }}
            onChange={(newValue): void => {
                if (betreuerWorkDay) {
                    setBetreuerWorkDay({
                        ...betreuerWorkDay,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderBetreuerWorkDayAbsent = () => (
        <BetreuerWorkDayAbsent
            value={betreuerWorkDay?.absent || ''}
            disabled={savingState === SavingState.saving}
            allowEmpty
            onChange={(newValue): void => {
                if (betreuerWorkDay) {
                    if (newValue) {
                        setBetreuerWorkDay({
                            ...betreuerWorkDay,
                            absent: newValue,
                            startTime1: null,
                            startTime1S: '',
                            stopTime1: null,
                            stopTime1S: '',
                            break1: null,
                            break1S: '',
                            startTime2: null,
                            startTime2S: '',
                            stopTime2: null,
                            stopTime2S: '',
                            break2: null,
                            break2S: '',
                        });
                    } else {
                        setBetreuerWorkDay({
                            ...betreuerWorkDay,
                            absent: newValue,
                        });
                    }
                }
            }}
        />
    );

    const renderTime = (
        column: 'startTime1' | 'stopTime1' | 'break1' | 'startTime2' | 'stopTime2' | 'break2',
        label: string,
    ) => {
        let columnS: 'startTime1S' | 'stopTime1S' | 'break1S' | 'startTime2S' | 'stopTime2S' | 'break2S';

        switch (column) {
            case 'startTime1':
                columnS = 'startTime1S';
                break;
            case 'stopTime1':
                columnS = 'stopTime1S';
                break;
            case 'break1':
                columnS = 'break1S';
                break;
            case 'startTime2':
                columnS = 'startTime2S';
                break;
            case 'stopTime2':
                columnS = 'stopTime2S';
                break;
            default:
                columnS = 'break2S';
                break;
        }

        return (
            <TextField
                label={label}
                value={betreuerWorkDay?.[columnS] || ''}
                disabled={savingState === SavingState.saving}
                onChange={(event, newValue): void => {
                    const cleanedNewValue = (newValue === undefined ? '' : newValue).replaceAll(/[^0-9:]/g, '');

                    if (betreuerWorkDay) {
                        setBetreuerWorkDay({
                            ...betreuerWorkDay,
                            [columnS]: cleanedNewValue,
                        });
                    }
                }}
                onBlur={() => {
                    if (betreuerWorkDay) {
                        const valueS = (betreuerWorkDay?.[columnS] || '').trim();
                        const value = valueS === '' ? null : timeStringToMinutes(valueS) || 0;
                        const newValueS = minutesToTimeString(value);

                        setBetreuerWorkDay({
                            ...betreuerWorkDay,
                            [column]: value,
                            [columnS]: newValueS,
                        });
                    }
                }}
            />
        );
    };

    const day = moment(`${workYear}-${workMonth}-${workDay}`);

    return (
        <>
            {routerConfirmMessage.render()}
            <Dialog
                hidden={false}
                title={day.format('DD.MM.YYYY')}
                dialogContentProps={{
                    type: DialogType.normal,
                }}
                modalProps={{
                    isBlocking: true,
                    dragOptions: {
                        moveMenuItemText: 'Move',
                        closeMenuItemText: 'Close',
                        menu: ContextualMenu,
                    },
                }}
                minWidth="80%"
                maxWidth="80%"
                onDismiss={() => {
                    onClose(false);
                }}
            >
                <form className={formStyles.form}>
                    <div className={styles.worktimes}>
                        <div>{renderTime('startTime1', 'VM - Von')}</div>
                        <div>{renderTime('stopTime1', 'VM - Bis')}</div>
                        <div>{renderTime('break1', 'VM - Pause')}</div>
                        <div>{renderTime('startTime2', 'NM - Von')}</div>
                        <div>{renderTime('stopTime2', 'NM - Bis')}</div>
                        <div>{renderTime('break2', 'NM - Pause')}</div>
                        <div>{renderBetreuerWorkDayAbsent()}</div>
                        <div>{renderTextField('note', 'Anmerkung')}</div>
                    </div>

                    <DialogFooter>
                        {savingState === SavingState.saving ? (
                            <PrimaryButton text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={() => save()}
                                text="Speichern"
                                disabled={savingState === SavingState.notChanged || betreuerWorkDayOne.isLoading}
                            />
                        )}
                    </DialogFooter>

                    {validationError && <div className={formStyles.error}>{validationError}</div>}
                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}
                </form>
                {(betreuerOne.isLoading ||
                    (betreuerOne.isFetching && betreuerWorkDayOne.isLoading) ||
                    betreuerWorkDayOne.isFetching ||
                    !betreuerWorkDay) && <LoadingIndicator />}
            </Dialog>
        </>
    );
};
